/**
 * Pitch arithmetic for conventional 12-EDO and circle-of-fifths 31-EDO.
 *
 * Staff geometry and interval numbers use diatonic positions: `d = octave * 7
 * + letterIndex`, with C=0 … B=6. Sound and interval quality use tuning steps.
 * Keeping those coordinates separate lets microtonal notes retain conventional
 * staff spelling while playback follows their exact 31-EDO pitch.
 *
 * A 12-EDO pitch retains the original diatonic-index representation and derives
 * its accidental from the key. A 31-EDO pitch is an absolute EDO-step index;
 * its pitch class has one canonical spelling in the circle-of-fifths ordering.
 */

import type { Edo, Exercise, Letter, MusicKey } from './types';
import { measureCount } from './types';

/** Letter names in diatonic order. */
export const LETTERS: readonly Letter[] = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];

/** Semitones above C for each letter. */
const LETTER_SEMI = [0, 2, 4, 5, 7, 9, 11];

/** 31-EDO steps above C for the seven natural notes. */
const LETTER_31 = [0, 5, 10, 13, 18, 23, 28];

interface Spelling31 {
  readonly letter: Letter;
  readonly alteration: -2 | -1 | 0 | 1 | 2;
  /** C-flat belongs to the next written octave despite sounding below it. */
  readonly octaveShift?: 1;
}

/**
 * Canonical chromatic order in 31-EDO. Sharps and flats move two EDO steps,
 * which leaves room for the distinct enharmonic spellings between naturals.
 */
const SPELLINGS_31: readonly Spelling31[] = [
  { letter: 'C', alteration: 0 },
  { letter: 'D', alteration: -2 },
  { letter: 'C', alteration: 1 },
  { letter: 'D', alteration: -1 },
  { letter: 'C', alteration: 2 },
  { letter: 'D', alteration: 0 },
  { letter: 'E', alteration: -2 },
  { letter: 'D', alteration: 1 },
  { letter: 'E', alteration: -1 },
  { letter: 'D', alteration: 2 },
  { letter: 'E', alteration: 0 },
  { letter: 'F', alteration: -1 },
  { letter: 'E', alteration: 1 },
  { letter: 'F', alteration: 0 },
  { letter: 'G', alteration: -2 },
  { letter: 'F', alteration: 1 },
  { letter: 'G', alteration: -1 },
  { letter: 'F', alteration: 2 },
  { letter: 'G', alteration: 0 },
  { letter: 'A', alteration: -2 },
  { letter: 'G', alteration: 1 },
  { letter: 'A', alteration: -1 },
  { letter: 'G', alteration: 2 },
  { letter: 'A', alteration: 0 },
  { letter: 'B', alteration: -2 },
  { letter: 'A', alteration: 1 },
  { letter: 'B', alteration: -1 },
  { letter: 'A', alteration: 2 },
  { letter: 'B', alteration: 0 },
  { letter: 'C', alteration: -1, octaveShift: 1 },
  { letter: 'B', alteration: 1 },
] as const;

/** An exercise's tuning, with legacy exercises interpreted as 12-EDO. */
export const edoOf = (ex: Pick<Exercise, 'edo'>): Edo => ex.edo === 31 ? 31 : 12;

const mod = (n: number, modulus: number): number => ((n % modulus) + modulus) % modulus;

/** Build a diatonic index from a letter name and octave number. */
export const dOf = (letter: Letter, octave: number): number =>
  octave * 7 + LETTERS.indexOf(letter);

/** The letter name of a diatonic index. */
export const letterOf = (d: number): Letter => LETTERS[((d % 7) + 7) % 7];

/** The octave number of a diatonic index. */
export const octaveOf = (d: number): number => Math.floor(d / 7);

/** Scientific pitch name, e.g. `"F4"` — accidentals come from the key, not the name. */
export const nameOf = (d: number): string => letterOf(d) + octaveOf(d);

/** Parse a scientific pitch name such as `"Bb3"`/`"B3"` back to a diatonic index. */
export function parseName(name: string): number {
  const letter = name[0].toUpperCase() as Letter;
  const octave = parseInt(name.replace(/^[A-Ga-g][#b♯♭]*/, ''), 10);
  return dOf(letter, octave);
}

/** The staff position used to engrave a stored pitch. */
export function staffPositionOf(pitch: number, edo: Edo = 12): number {
  if (edo === 12) return pitch;
  const pc = mod(pitch, 31);
  const spelling = SPELLINGS_31[pc];
  const soundingOctave = Math.floor(pitch / 31) - 1;
  return dOf(spelling.letter, soundingOctave + (spelling.octaveShift ?? 0));
}

/** The accidental carried by a canonical 31-EDO pitch. */
export function spellingAlteration(pitch: number, edo: Edo = 12): number {
  return edo === 31 ? SPELLINGS_31[mod(pitch, 31)].alteration : 0;
}

/** Build the natural pitch on one staff position in the requested tuning. */
export function naturalPitchOf(d: number, edo: Edo = 12): number {
  if (edo === 12) return d;
  const letterIndex = LETTERS.indexOf(letterOf(d));
  return 31 * (octaveOf(d) + 1) + LETTER_31[letterIndex];
}

/**
 * Build a 31-EDO pitch from a diatonic position and a written alteration.
 * A single sharp or flat spans two EDO steps in this notation.
 */
export function pitch31Of(d: number, alteration = 0): number {
  return naturalPitchOf(d, 31) + alteration * 2;
}

/** Scientific pitch name in the selected notation, such as `Dbb4` or `Cx4`. */
export function nameOfPitch(pitch: number, edo: Edo = 12): string {
  if (edo === 12) return nameOf(pitch);
  const spelling = SPELLINGS_31[mod(pitch, 31)];
  const accidental = ['bb', 'b', '', '#', 'x'][spelling.alteration + 2];
  const soundingOctave = Math.floor(pitch / 31) - 1;
  return `${spelling.letter}${accidental}${soundingOctave + (spelling.octaveShift ?? 0)}`;
}

/** Parse a scientific pitch name in the selected notation. */
export function parsePitchName(name: string, edo: Edo = 12): number {
  if (edo === 12) return parseName(name);
  const match = name.trim().match(/^([A-Ga-g])(bb|##|x|b|#|𝄫|𝄪|♭|♯)?(-?\d+)$/);
  if (!match) return Number.NaN;
  const letter = match[1].toUpperCase() as Letter;
  const alteration = accidentalValue(match[2] ?? '');
  return pitch31Of(dOf(letter, Number(match[3])), alteration);
}

function accidentalValue(token: string): number {
  if (token === 'bb' || token === '𝄫') return -2;
  if (token === 'b' || token === '♭') return -1;
  if (token === '#' || token === '♯') return 1;
  if (token === '##' || token === 'x' || token === '𝄪') return 2;
  return 0;
}

const SHARP_ORDER = 'FCGDAEB';
const FLAT_ORDER = 'BEADGCF';

/** The alteration a key signature applies to a letter: -1, 0 or +1. */
export function signatureAlteration(letter: Letter, sig: number): number {
  if (sig > 0) return SHARP_ORDER.indexOf(letter) < sig ? 1 : 0;
  if (sig < 0) return FLAT_ORDER.indexOf(letter) < -sig ? -1 : 0;
  return 0;
}

/** The letter one diatonic step below the tonic — the leading tone. */
export const leadingLetter = (key: MusicKey): Letter =>
  letterOf(dOf(key.tonic, 4) - 1);

/**
 * The alteration in force for a letter in a given measure: the key signature,
 * plus the raised leading tone that minor keys take in the penultimate bar.
 */
export function alterationFor(letter: Letter, measure: number, ex: Exercise): number {
  let a = signatureAlteration(letter, ex.key.sig);
  if (ex.key.mode === 'minor' && measure === measureCount(ex) - 2 && letter === leadingLetter(ex.key)) {
    a += 1;
  }
  return a;
}

/** Pitch produced by one unmarked staff position under the exercise's key. */
export function pitchAtStaffPosition(d: number, measure: number, ex: Exercise): number {
  if (edoOf(ex) === 12) return d;
  return pitch31Of(d, alterationFor(letterOf(d), measure, ex));
}

/** The number of tuning steps represented by a stored pitch. */
export function tuningStepOf(pitch: number, measure: number, ex: Exercise): number {
  if (edoOf(ex) === 31) return pitch;
  const letter = letterOf(pitch);
  return 12 * (octaveOf(pitch) + 1) + LETTER_SEMI[LETTERS.indexOf(letter)] + alterationFor(letter, measure, ex);
}

/** Signed tuning-step distance between two pitches in one measure. */
export const tuningStepsBetween = (upper: number, lower: number, measure: number, ex: Exercise): number =>
  tuningStepOf(upper, measure, ex) - tuningStepOf(lower, measure, ex);

/** Signed diatonic staff distance between two stored pitches. */
export const diatonicDistance = (upper: number, lower: number, ex: Exercise): number =>
  staffPositionOf(upper, edoOf(ex)) - staffPositionOf(lower, edoOf(ex));

/** MIDI-compatible pitch number for playback in the context of one measure. */
export function midiOf(d: number, measure: number, ex: Exercise): number {
  return tuningStepOf(d, measure, ex) * 12 / edoOf(ex);
}

/**
 * MIDI note number ignoring the cadential accidental — used by the cantus firmus
 * generator, which reasons about a bare scale before an exercise exists.
 */
export function midiInKey(d: number, key: MusicKey): number {
  const letter = letterOf(d);
  return 12 * (octaveOf(d) + 1) + LETTER_SEMI[LETTERS.indexOf(letter)] + signatureAlteration(letter, key.sig);
}

/** The accidental glyph a note should be drawn with, or `null` for none. */
export function accidentalGlyph(alteration: number, explicitNatural = false): '𝄪' | '♯' | '♭' | '𝄫' | '♮' | null {
  if (alteration >= 2) return '𝄪';
  if (alteration === 1) return '♯';
  if (alteration <= -2) return '𝄫';
  if (alteration === -1) return '♭';
  return explicitNatural ? '♮' : null;
}

/**
 * Accidental that must be printed beside a note. In 31-EDO every pitch carries
 * a spelling; the key signature suppresses a matching accidental.
 */
export function notatedAccidentalGlyph(pitch: number, measure: number, ex: Exercise): ReturnType<typeof accidentalGlyph> {
  const edo = edoOf(ex);
  if (edo === 31) {
    const d = staffPositionOf(pitch, edo);
    const alteration = spellingAlteration(pitch, edo);
    const signature = signatureAlteration(letterOf(d), ex.key.sig);
    return alteration === signature ? null : accidentalGlyph(alteration, true);
  }
  if (ex.key.mode !== 'minor' || measure !== measureCount(ex) - 2) return null;
  if (letterOf(pitch) !== leadingLetter(ex.key)) return null;
  return accidentalGlyph(alterationFor(letterOf(pitch), measure, ex), true);
}

/** English ordinal for an interval number: 1 → "1st", 2 → "2nd", 11 → "11th". */
export function ordinal(n: number): string {
  const teens = n % 100;
  if (teens >= 11 && teens <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
}
